/**
 * Founder file manager. Three sections: pitch deck, Venture Readiness Client
 * Agreement (VRCA), and general documents. Pitch deck and VRCA are versioned —
 * a new upload becomes "current" and the prior one moves to history; general
 * documents are a flat, deletable list. All uploads are PDF-only.
 *
 * Uploads update local state optimistically (marking the new file current and
 * flipping older ones to is_current: false) without a re-fetch.
 */
import React, { useEffect, useRef, useState } from 'react';
import { deleteGeneralFile, getMyFiles, uploadGeneralFile, uploadPitchDeck, uploadVRCA } from '../services/apiService';

// Short, human-readable date (e.g. "Jan 5, 2026"); em dash when absent.
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Fetch the file (with cookies) and force a client-side download under the
// given filename, rather than navigating to the (possibly inline-rendered) URL.
function triggerBlobDownload(url, filename) {
  fetch(url, { credentials: 'include' })
    .then((r) => r.blob())
    .then((blob) => {
      const burl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = burl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(burl);
    })
    .catch(() => {});
}

function IconDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconEye() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6M9 6V4h6v2" />
    </svg>
  );
}

/** Founder document hub: versioned pitch deck and VRCA plus general documents. */
export default function Files() {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [vrcaUploading, setVrcaUploading] = useState(false);
  const [vrcaError, setVrcaError] = useState(null);
  const [generalUploading, setGeneralUploading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [pitchDeckUploading, setPitchDeckUploading] = useState(false);
  const [pitchDeckError, setPitchDeckError] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showPitchDeckHistory, setShowPitchDeckHistory] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const vrcaRef = useRef(null);
  const generalRef = useRef(null);
  const pitchDeckRef = useRef(null);

  useEffect(() => {
    getMyFiles()
      .then(setFiles)
      .catch(() => setLoadError('Failed to load files.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleVRCAUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setVrcaUploading(true);
    setVrcaError(null);
    try {
      const uploaded = await uploadVRCA(file);
      setFiles((prev) => ({
        ...prev,
        vrca: {
          current: uploaded,
          versions: [uploaded, ...(prev.vrca.versions || []).map((v) => ({ ...v, is_current: false }))],
        },
      }));
      setShowVersionHistory(false);
    } catch (err) {
      setVrcaError(err.message || 'Upload failed.');
    } finally {
      setVrcaUploading(false);
    }
  }

  async function handleGeneralUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setGeneralUploading(true);
    setGeneralError(null);
    try {
      const uploaded = await uploadGeneralFile(file);
      setFiles((prev) => ({ ...prev, general: [uploaded, ...prev.general] }));
    } catch (err) {
      setGeneralError(err.message || 'Upload failed.');
    } finally {
      setGeneralUploading(false);
    }
  }

  async function handleDelete(fileId) {
    setDeletingId(fileId);
    try {
      await deleteGeneralFile(fileId);
      setFiles((prev) => ({ ...prev, general: prev.general.filter((f) => f.id !== fileId) }));
    } catch {
      // silent
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <div style={styles.layout}>
        <div style={styles.main}>
          <p style={styles.dim}>Loading…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.layout}>
        <div style={styles.main}>
          <p style={styles.danger}>{loadError}</p>
        </div>
      </div>
    );
  }

  async function handlePitchDeckUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setPitchDeckUploading(true);
    setPitchDeckError(null);
    try {
      const uploaded = await uploadPitchDeck(file);
      setFiles((prev) => ({
        ...prev,
        pitch_deck: {
          current: uploaded,
          versions: [uploaded, ...(prev.pitch_deck?.versions || []).map((v) => ({ ...v, is_current: false }))],
        },
      }));
      setShowPitchDeckHistory(false);
    } catch (err) {
      setPitchDeckError(err.message || 'Upload failed.');
    } finally {
      setPitchDeckUploading(false);
    }
  }

  const { vrca, general, pitch_deck } = files;
  const pitchDeckCurrent = pitch_deck?.current;
  const pitchDeckVersions = pitch_deck?.versions || [];
  const pitchDeckHistory = pitchDeckVersions.filter((v) => !v.is_current);

  const vrcaCurrent = vrca?.current;
  const vrcaVersions = vrca?.versions || [];
  const historicalVersions = vrcaVersions.filter((v) => !v.is_current);

  return (
    <div style={styles.layout}>
      <div style={styles.main}>
        <div style={styles.wrapper}>

          <div style={styles.header}>
            <h1 style={styles.pageTitle}>Files</h1>
            <p style={styles.subtitle}>
              Store and share important documents with your EIR.
            </p>
          </div>

          {/* ── Pitch Deck ── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <h2 style={styles.cardTitle}>Pitch Deck</h2>
                <p style={styles.cardSubtitle}>
                  Your pitch deck is shared with your EIR and program staff. Upload the latest version anytime.
                </p>
              </div>
              <div style={{ flexShrink: 0 }}>
                <input ref={pitchDeckRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handlePitchDeckUpload} />
                <button
                  style={styles.actionBtn}
                  onClick={() => pitchDeckRef.current?.click()}
                  disabled={pitchDeckUploading}
                >
                  {pitchDeckUploading ? 'Uploading…' : pitchDeckCurrent ? 'Replace' : 'Upload Pitch Deck'}
                </button>
              </div>
            </div>

            {pitchDeckError && <p style={styles.danger}>{pitchDeckError}</p>}

            {pitchDeckCurrent ? (
              <div style={{ marginTop: 16 }}>
                <div style={styles.fileRow}>
                  <span style={styles.versionBadge}>Current · v{pitchDeckCurrent.version_number}</span>
                  <span style={styles.fileName}>{pitchDeckCurrent.original_filename}</span>
                  <span style={styles.fileMeta}>{formatDate(pitchDeckCurrent.uploaded_at)}</span>
                  <button style={styles.iconBtn} onClick={() => window.open(pitchDeckCurrent.url, '_blank')}>
                    <IconEye /><span>Preview</span>
                  </button>
                  <button style={styles.iconBtn} onClick={() => triggerBlobDownload(pitchDeckCurrent.url, pitchDeckCurrent.original_filename)}>
                    <IconDownload /><span>Download</span>
                  </button>
                </div>
                {pitchDeckHistory.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <button style={styles.versionToggle} onClick={() => setShowPitchDeckHistory((v) => !v)}>
                      {showPitchDeckHistory ? 'Hide' : 'Show'} version history ({pitchDeckHistory.length})
                    </button>
                    {showPitchDeckHistory && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {pitchDeckHistory.map((v) => (
                          <div key={v.id} style={{ ...styles.fileRow, background: 'rgba(255,255,255,0.5)' }}>
                            <span style={{ ...styles.fileMeta, minWidth: 28 }}>v{v.version_number}</span>
                            <span style={styles.fileName}>{v.original_filename}</span>
                            <span style={styles.fileMeta}>{formatDate(v.uploaded_at)}</span>
                            <button style={styles.iconBtn} onClick={() => window.open(v.url, '_blank')}>
                              <IconEye /><span>Preview</span>
                            </button>
                            <button style={styles.iconBtn} onClick={() => triggerBlobDownload(v.url, v.original_filename)}>
                              <IconDownload /><span>Download</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.emptyState}>
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>No pitch deck uploaded yet.</p>
                <p style={{ margin: 0, fontSize: 13, opacity: 0.7 }}>
                  You can upload a pitch deck now or include one when applying to the program.
                </p>
              </div>
            )}
          </div>

          {/* ── Venture Readiness Client Agreement ── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <h2 style={styles.cardTitle}>Venture Readiness Client Agreement</h2>
                <p style={styles.cardSubtitle}>
                  Upload your signed client agreement. New uploads replace the current version and are saved to history.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <span style={{ fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, fontFamily: 'var(--font-body)' }}>Blank template:</span>
                  <button
                    style={styles.iconBtn}
                    onClick={() => window.open('/api/vr/templates/VRP_Client_Agreement_2025.pdf', '_blank')}
                  >
                    <IconEye /><span>View</span>
                  </button>
                  <button
                    style={styles.iconBtn}
                    onClick={() => triggerBlobDownload('/api/vr/templates/VRP_Client_Agreement_2025.pdf', 'VRP Client Agreement 2025.pdf')}
                  >
                    <IconDownload /><span>Download</span>
                  </button>
                </div>
              </div>
              <div style={{ flexShrink: 0 }}>
                <input ref={vrcaRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleVRCAUpload} />
                <button
                  style={styles.actionBtn}
                  onClick={() => vrcaRef.current?.click()}
                  disabled={vrcaUploading}
                >
                  {vrcaUploading ? 'Uploading…' : vrcaCurrent ? 'Replace' : 'Upload Agreement'}
                </button>
              </div>
            </div>

            {vrcaError && <p style={styles.danger}>{vrcaError}</p>}

            {vrcaCurrent ? (
              <div style={{ marginTop: 16 }}>
                <div style={styles.fileRow}>
                  <span style={styles.versionBadge}>Current · v{vrcaCurrent.version_number}</span>
                  <span style={styles.fileName}>{vrcaCurrent.original_filename}</span>
                  <span style={styles.fileMeta}>{formatDate(vrcaCurrent.uploaded_at)}</span>
                  <button style={styles.iconBtn} onClick={() => window.open(vrcaCurrent.url, '_blank')}>
                    <IconEye /><span>Preview</span>
                  </button>
                  <button style={styles.iconBtn} onClick={() => triggerBlobDownload(vrcaCurrent.url, vrcaCurrent.original_filename)}>
                    <IconDownload /><span>Download</span>
                  </button>
                </div>
                {historicalVersions.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <button style={styles.versionToggle} onClick={() => setShowVersionHistory((v) => !v)}>
                      {showVersionHistory ? 'Hide' : 'Show'} version history ({historicalVersions.length})
                    </button>
                    {showVersionHistory && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {historicalVersions.map((v) => (
                          <div key={v.id} style={{ ...styles.fileRow, background: 'rgba(255,255,255,0.5)' }}>
                            <span style={{ ...styles.fileMeta, minWidth: 28 }}>v{v.version_number}</span>
                            <span style={styles.fileName}>{v.original_filename}</span>
                            <span style={styles.fileMeta}>{formatDate(v.uploaded_at)}</span>
                            <button style={styles.iconBtn} onClick={() => window.open(v.url, '_blank')}>
                              <IconEye /><span>Preview</span>
                            </button>
                            <button style={styles.iconBtn} onClick={() => triggerBlobDownload(v.url, v.original_filename)}>
                              <IconDownload /><span>Download</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.emptyState}>
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>No agreement uploaded yet.</p>
                <p style={{ margin: 0, fontSize: 13, opacity: 0.7 }}>
                  Upload your signed Venture Readiness Client Agreement to share it with your EIR.
                </p>
              </div>
            )}
          </div>

          {/* ── General Documents ── */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Documents</h2>
              <div style={{ flexShrink: 0 }}>
                <input ref={generalRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleGeneralUpload} />
                <button style={styles.actionBtn} onClick={() => generalRef.current?.click()} disabled={generalUploading}>
                  {generalUploading ? 'Uploading…' : '+ Upload Document'}
                </button>
              </div>
            </div>

            {generalError && <p style={styles.danger}>{generalError}</p>}

            {general.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={{ margin: 0 }}>No documents uploaded yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                {general.map((f) => (
                  <div key={f.id} style={styles.fileRow}>
                    <span style={styles.fileName}>{f.original_filename}</span>
                    <span style={styles.fileMeta}>{formatDate(f.uploaded_at)}</span>
                    <button style={styles.iconBtn} onClick={() => window.open(f.url, '_blank')}>
                      <IconEye /><span>Preview</span>
                    </button>
                    <button style={styles.iconBtn} onClick={() => triggerBlobDownload(f.url, f.original_filename)}>
                      <IconDownload /><span>Download</span>
                    </button>
                    <button
                      style={{ ...styles.iconBtn, ...styles.dangerBtn, opacity: deletingId === f.id ? 0.5 : 1 }}
                      onClick={() => handleDelete(f.id)}
                      disabled={deletingId === f.id}
                      title="Delete"
                    >
                      <IconTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed',
  },
  main: {
    flex: 1,
    overflowY: 'auto',
    padding: 0,
  },
  wrapper: {
    padding: 'var(--space-8) var(--space-6)',
    maxWidth: '98%',
    margin: '0 auto',
  },
  header: {
    marginBottom: 'var(--space-6)',
    paddingTop: 'var(--space-4)',
  },
  pageTitle: {
    fontFamily: 'var(--font-accent)',
    fontSize: 'var(--font-size-page-title)',
    fontWeight: 'var(--font-weight-page-title)',
    color: 'var(--color-rust)',
    lineHeight: 1.1,
    marginBottom: 'var(--space-2)',
  },
  subtitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 22,
    fontWeight: 'var(--font-weight-page-subtitle)',
    color: 'var(--color-forest)',
    opacity: 0.6,
    lineHeight: 1.5,
  },
  card: {
    background: 'var(--color-frost)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-card)',
    padding: '24px 28px',
    marginBottom: 20,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 4,
  },
  cardTitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--color-forest)',
    margin: '0 0 4px',
  },
  cardSubtitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-forest)',
    opacity: 0.6,
    margin: 0,
  },
  actionBtn: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--color-forest)',
    background: '#fff',
    border: '1.5px solid var(--color-mint-dark)',
    borderRadius: 'var(--radius-sm)',
    padding: '7px 16px',
    cursor: 'pointer',
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    background: 'var(--color-mint)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-mint-dark)',
  },
  fileName: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--color-forest)',
    fontFamily: 'var(--font-body)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileMeta: {
    fontSize: 12,
    color: 'var(--color-forest)',
    opacity: 0.55,
    fontFamily: 'var(--font-body)',
    whiteSpace: 'nowrap',
  },
  versionBadge: {
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 9999,
    background: '#dcfce7',
    color: '#15803d',
    fontFamily: 'var(--font-body)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  iconBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 11px',
    borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--color-mint-dark)',
    background: '#fff',
    color: 'var(--color-forest)',
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  dangerBtn: {
    border: '1.5px solid #fca5a5',
    color: '#dc2626',
    padding: '5px 8px',
  },
  versionToggle: {
    background: 'none',
    border: 'none',
    color: 'var(--color-forest)',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    opacity: 0.65,
    textDecoration: 'underline',
  },
  emptyState: {
    textAlign: 'center',
    padding: '28px 0',
    color: 'var(--color-forest)',
    opacity: 0.55,
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    marginTop: 8,
  },
  dim: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.5, fontStyle: 'italic', padding: 'var(--space-8)' },
  danger: { color: '#dc2626', fontSize: 13, fontFamily: 'var(--font-body)', margin: '8px 0 0' },
};
